import { LOCALES, type AccessibilityPreferences, type Locale, type PresentationPreferences } from '../data/presentation-types';

/**
 * Persistance locale, versionnée, des PRÉFÉRENCES D'INTERFACE UNIQUEMENT — jamais le mode du
 * jour ni l'état émotionnel, qui restent portés par `DailyRoutineDetails` via les use cases
 * applicatifs (`ChangeDailyMode`, `SetEmotionalState`) et la persistance Dexie déjà en place.
 * Ne stocke ni missions ni progression : cette couche ne connaît que l'UI.
 */
export const UI_PREFERENCES_STORAGE_KEY = 'ascend.ui.preferences.v1';

const CURRENT_VERSION = 1;

export type LayoutDensity = 'comfortable' | 'compact';

/**
 * Préférence purement cosmétique (espacement des lignes de mission) — distincte de la
 * `density` fonctionnelle de `ResolvedPresentation`, qui reste dérivée du mode seul et n'est
 * jamais transmise à `resolvePresentation` (voir invariant testé dans
 * `data/resolvePresentation.test.ts`).
 */
export interface StoredUiPreferences {
  readonly version: 1;
  readonly locale: Locale;
  readonly firstName: string;
  /** `null` = aucun choix explicite : la valeur effective suit le point de rupture responsive. */
  readonly headerExpanded: boolean | null;
  readonly thoughtExpanded: boolean;
  readonly layoutDensity: LayoutDensity;
  readonly presentationPreferences: PresentationPreferences;
  readonly accessibilityPreferences: AccessibilityPreferences;
}

export const DEFAULT_UI_PREFERENCES: StoredUiPreferences = {
  version: CURRENT_VERSION,
  locale: 'fr-FR',
  firstName: '',
  headerExpanded: null,
  thoughtExpanded: false,
  layoutDensity: 'comfortable',
  presentationPreferences: {
    showProgress: true,
    showEditorialCards: true,
    showTimes: true,
    locale: 'fr-FR',
  },
  accessibilityPreferences: { ags: false, reducedMotion: false },
};

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

function isLayoutDensity(value: unknown): value is LayoutDensity {
  return value === 'comfortable' || value === 'compact';
}

function isPresentationPreferences(value: unknown): value is PresentationPreferences {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    isBoolean(candidate.showProgress) &&
    isBoolean(candidate.showEditorialCards) &&
    isBoolean(candidate.showTimes) &&
    isLocale(candidate.locale)
  );
}

function isAccessibilityPreferences(value: unknown): value is AccessibilityPreferences {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return isBoolean(candidate.ags) && isBoolean(candidate.reducedMotion);
}

/** Valide intégralement la forme lue — un seul champ invalide ou une version inconnue rejette tout le bloc. */
function isStoredUiPreferences(value: unknown): value is StoredUiPreferences {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.version === CURRENT_VERSION &&
    isLocale(candidate.locale) &&
    (typeof candidate.firstName === 'string' || candidate.firstName === undefined) &&
    (candidate.headerExpanded === null || isBoolean(candidate.headerExpanded)) &&
    isBoolean(candidate.thoughtExpanded) &&
    isLayoutDensity(candidate.layoutDensity) &&
    isPresentationPreferences(candidate.presentationPreferences) &&
    isAccessibilityPreferences(candidate.accessibilityPreferences)
  );
}

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

function safeLocalStorage(): Storage | undefined {
  try {
    const storage = globalThis.localStorage;
    storage.getItem(UI_PREFERENCES_STORAGE_KEY);
    return storage;
  } catch {
    return undefined;
  }
}

/**
 * Lit les préférences persistées. Ne lève jamais : `localStorage` indisponible, JSON invalide,
 * forme inattendue ou version de stockage inconnue retombent tous silencieusement sur
 * `DEFAULT_UI_PREFERENCES`.
 */
export function loadUiPreferences(storage: ReadableStorage | undefined = safeLocalStorage()): StoredUiPreferences {
  if (!storage) return DEFAULT_UI_PREFERENCES;
  try {
    const raw = storage.getItem(UI_PREFERENCES_STORAGE_KEY);
    if (!raw) return DEFAULT_UI_PREFERENCES;
    const parsed: unknown = JSON.parse(raw);
    return isStoredUiPreferences(parsed) ? parsed : DEFAULT_UI_PREFERENCES;
  } catch {
    return DEFAULT_UI_PREFERENCES;
  }
}

/** Écrit les préférences. N'écrase que sa propre clé ; échoue silencieusement (quota, navigation privée). */
export function saveUiPreferences(
  preferences: StoredUiPreferences,
  storage: WritableStorage | undefined = safeLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    /* quota dépassé ou navigation privée — la session continue en mémoire pour cet onglet */
  }
}

/**
 * Distingue « rien n'a jamais été stocké » (premier lancement — la locale navigateur doit être
 * détectée) de « des préférences par défaut ont déjà été enregistrées explicitement ».
 */
export function hasStoredUiPreferences(storage: ReadableStorage | undefined = safeLocalStorage()): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(UI_PREFERENCES_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}
